import { AiPlatform, AssistantSettings } from '../script';
import { IChatbotAiKnowledgeCursorData } from './ai-knowledge.model';

export const CHATBOT_AI_ASSISTANT_CONFIG = {
  pageSize: 50,
  previewPageSize: 100,
  promptPageSize: 500,
  maxProductsPerAdd: 15,
  productPlatform: 1,
  manualUrl: 'https://vpage.manual.nhanh.vn/chatbot-ai/tro-ly',
  status: {
    enabled: 1,
    disabled: 2,
  },
} as const;

export const CHATBOT_AI_ASSISTANT_MODELS: Readonly<Record<AiPlatform, ReadonlyArray<{
  label: string;
  value: string;
}>>> = {
  [AiPlatform.OpenAI]: [
    { label: 'GPT-5.6 Sol', value: 'gpt-5.6-sol' },
    { label: 'GPT-5.6 Terra', value: 'gpt-5.6-terra' },
    { label: 'GPT-5.6 Luna', value: 'gpt-5.6-luna' },
    { label: 'GPT-4o', value: 'gpt-4o' },
    { label: 'GPT-4o Mini', value: 'gpt-4o-mini' },
  ],
  [AiPlatform.Claude]: [
    { label: 'Claude Fable 5', value: 'claude-fable-5' },
    { label: 'Claude Opus 5', value: 'claude-opus-5' },
    { label: 'Claude Sonnet 5', value: 'claude-sonnet-5' },
    { label: 'Claude Haiku 4.5', value: 'claude-haiku-4-5-20251001' },
  ],
  [AiPlatform.DeepSeek]: [
    { label: 'DeepSeek V4 Flash', value: 'deepseek-flash' },
    { label: 'DeepSeek V4 Pro', value: 'deepseek-v4-pro' },
  ],
  [AiPlatform.Gemini]: [
    { label: 'Gemini 3.7 Flash', value: 'gemini-3.7-flash' },
    { label: 'Gemini 3.6 Flash', value: 'gemini-3.6-flash' },
    { label: 'Gemini 3.5 Flash', value: 'gemini-3.5-flash' },
    { label: 'Gemini 3.5 Flash Lite', value: 'gemini-3.5-flash-lite' },
    { label: 'Gemini 3.1 Flash Lite', value: 'gemini-3.1-flash-lite' },
    { label: 'Gemini 3.1 Pro Preview', value: 'gemini-3.1-pro-preview' },
    { label: 'Gemini 3 Flash Preview', value: 'gemini-3-flash-preview' },
  ],
  [AiPlatform.Grok]: [
    { label: 'Grok 4.6', value: 'grok-4.6' },
    { label: 'Grok 4.5', value: 'grok-4.5' },
    { label: 'Grok 4.3', value: 'grok-4.3' },
  ],
};

export type ChatbotAiAssistantStatus =
  (typeof CHATBOT_AI_ASSISTANT_CONFIG.status)[keyof typeof CHATBOT_AI_ASSISTANT_CONFIG.status];

export interface IChatbotAiAssistant {
  id: string;
  name: string;
  businessId: number | string;
  description?: string | null;
  pageId?: string | null;
  status: ChatbotAiAssistantStatus;
  type: number;
  platform: AiPlatform | 0;
  accountId: string | null;
  model: string | null;
  assistantId?: string | null;
  knowledgeId: string[];
  prompt: string | null;
  settings: AssistantSettings;
  createdById: string;
  createdAt: number;
  countRequests?: number;
  countTokens?: number;
}

export type IChatbotAiAssistantCursorData<T> = IChatbotAiKnowledgeCursorData<T>;

export interface IChatbotAiAssistantListParams {
  size?: number;
  next?: string;
  isReport?: boolean;
}

export interface IChatbotAiAssistantSaveParams {
  id?: string;
  name: string;
  accountId?: string;
  platform?: AiPlatform | 0;
  model?: string;
  prompt?: string;
  knowledgeId?: string[];
  status?: ChatbotAiAssistantStatus;
  settings?: AssistantSettings;
}

export interface IChatbotAiAssistantIdentityParams {
  id: string;
}

export interface IChatbotAiAssistantDetailData {
  result: IChatbotAiAssistant | null;
}

export interface IChatbotAiAssistantStatusParams extends IChatbotAiAssistantIdentityParams {
  status: ChatbotAiAssistantStatus;
}

export interface IChatbotAiAssistantMessageData {
  message: string;
}

export interface IChatbotAiKnowledgeProduct {
  id: string;
  assistantId?: string;
  platform?: number;
  productId: string;
  product: IChatbotAiProduct;
  embeddingIds?: string[];
  createdAt?: number;
  createdById?: string;
}

export interface IChatbotAiKnowledgeProductListParams {
  assistantId: string;
  size?: number;
  next?: string;
}

export interface IChatbotAiProduct {
  id: number | string;
  name: string;
  code?: string | null;
  price?: number;
  imageUrl?: string;
  [key: string]: unknown;
}

export interface IChatbotAiKnowledgeProductSearchParams {
  name: string;
}

export interface IChatbotAiKnowledgeProductSearchData {
  result: IChatbotAiProduct[];
}

export interface IChatbotAiKnowledgeProductAddItem {
  assistantId: string;
  platform: number;
  productId: string;
  product: string;
}

export interface IChatbotAiKnowledgeProductAddParams {
  products: IChatbotAiKnowledgeProductAddItem[];
}

export interface IChatbotAiKnowledgeProductAddData {
  message: string;
  result: IChatbotAiKnowledgeProduct[];
}

export interface IChatbotAiKnowledgeProductRemoveData {
  result: string;
}

export interface IChatbotAiIndustryCategory {
  id: string;
  name: string;
  status: number;
  createdById?: string;
  createdAt?: number;
  createdByName?: string;
}

export interface IChatbotAiIndustryCategoryData {
  result: IChatbotAiIndustryCategory[];
}

export interface IChatbotAiPromptCategory {
  id: string;
  name: string;
}

export interface IChatbotAiPromptTemplate {
  id: string;
  name: string;
  description: string;
  tasks: string[];
  prompt: string;
  status: number;
  categories: IChatbotAiPromptCategory[];
  createdById?: string;
  createdAt?: number;
  createdByName?: string;
}

export interface IChatbotAiPromptListParams {
  name?: string;
  status?: number;
  categoryId?: string;
  startCreated?: string;
  endCreated?: string;
  limit?: number;
  next?: string;
}

export interface IChatbotAiPromptListData {
  result: IChatbotAiPromptTemplate[];
  next: string | false;
}

export interface IChatbotAiPreviewMessage {
  id: string;
  conversationId: string;
  content: string | string[];
  type: number;
  isUser: boolean;
  createdAt: number;
}

export interface IChatbotAiPreviewChatParams {
  conversationId: string;
  assistantId: string;
  message: string;
}

export interface IChatbotAiPreviewChatData {
  messages: Array<Pick<IChatbotAiPreviewMessage, 'content' | 'type'>>;
  conversationId: string;
}

export interface IChatbotAiPreviewListMessageParams {
  conversationId: string;
  size?: number;
  next?: string;
}
